/*
 * Copyright (c) Jupyter Development Team.
 * Distributed under the terms of the Modified BSD License.
 */
import { deleteLine, toggleBlockComment, toggleComment, toggleTabFocusMode } from '@codemirror/commands';
import { foldable, foldAll, foldCode, foldEffect, foldState, unfoldAll, unfoldCode, unfoldEffect } from '@codemirror/language';
import { EditorView } from '@codemirror/view';
import { selectNextOccurrence } from '@codemirror/search';
import { ITranslator, nullTranslator } from '@jupyterlab/translation';
import { INotebookTracker } from '@jupyterlab/notebook';
import { ICommandPalette } from '@jupyterlab/apputils';
/**
 * Identifiers of commands.
 */
var CommandIDs;
(function (CommandIDs) {
    CommandIDs.deleteLine = 'codemirror:delete-line';
    CommandIDs.toggleBlockComment = 'codemirror:toggle-block-comment';
    CommandIDs.toggleComment = 'codemirror:toggle-comment';
    CommandIDs.selectNextOccurrence = 'codemirror:select-next-occurrence';
    CommandIDs.toggleTabFocusMode = 'codemirror:toggle-tab-focus-mode';
    CommandIDs.foldCurrent = 'codemirror:fold-current';
    CommandIDs.unfoldCurrent = 'codemirror:unfold-current';
    CommandIDs.foldSubregions = 'codemirror:fold-subregions';
    CommandIDs.unfoldSubregions = 'codemirror:unfold-subregions';
    CommandIDs.foldAll = 'codemirror:fold-all';
    CommandIDs.unfoldAll = 'codemirror:unfold-all';
})(CommandIDs || (CommandIDs = {}));
/**
 * Selector for CodeMirror editor with `cmTile` attribute.
 */
const CODE_MIRROR_SELECTOR = '.cm-content';
/**
 * The editor commands.
 */
export const commandsPlugin = {
    id: '@jupyterlab/codemirror-extension:commands',
    description: 'Registers commands acting on selected/active CodeMirror editor.',
    autoStart: true,
    requires: [INotebookTracker],
    optional: [ITranslator, ICommandPalette],
    activate: (app, tracker, translator, palette) => {
        translator = translator !== null && translator !== void 0 ? translator : nullTranslator;
        const trans = translator.load('jupyterlab');
        const isCodeMirror = (node) => node.classList.contains(CODE_MIRROR_SELECTOR);
        const findEditorView = () => {
            var _a, _b;
            const node = (_a = app.contextMenuHitTest(isCodeMirror)) !== null && _a !== void 0 ? _a : (_b = document.activeElement) === null || _b === void 0 ? void 0 : _b.closest(CODE_MIRROR_SELECTOR);
            if (!node) {
                return;
            }
            return EditorView.findFromDOM(node);
        };
        const isEnabled = () => {
            return !!findEditorView();
        };
        const getActiveEditorInfo = () => {
            var _a, _b;
            const currentWidget = app.shell.currentWidget;
            // Check for active notebook
            const notebook = (_a = tracker.currentWidget) === null || _a === void 0 ? void 0 : _a.content;
            if (tracker.currentWidget !== null &&
                tracker.currentWidget === currentWidget &&
                notebook &&
                notebook.activeCell) {
                return {
                    type: 'notebook',
                    widget: currentWidget,
                    notebook,
                    isEnabled: true
                };
            }
            // Check for file editor
            const fileEditorWidget = currentWidget;
            if (fileEditorWidget &&
                fileEditorWidget.content &&
                typeof ((_b = fileEditorWidget.content.editor) === null || _b === void 0 ? void 0 : _b.focus) === 'function') {
                return {
                    type: 'fileEditor',
                    widget: fileEditorWidget,
                    isEnabled: true
                };
            }
            return {
                type: 'none',
                widget: null,
                isEnabled: false
            };
        };
        // Simplified isEnabled function using the helper
        const isCodeFoldingEnabled = () => {
            return getActiveEditorInfo().isEnabled;
        };
        app.commands.addCommand(CommandIDs.deleteLine, {
            label: trans.__('Delete the current line'),
            describedBy: {
                args: {
                    type: 'object',
                    properties: {}
                }
            },
            execute: () => {
                const view = findEditorView();
                if (!view) {
                    return;
                }
                deleteLine(view);
            },
            isEnabled
        });
        app.commands.addCommand(CommandIDs.toggleBlockComment, {
            label: trans.__('Toggle Block Comment'),
            caption: trans.__('Toggles block comments in languages which support it (e.g. C, JavaScript)'),
            describedBy: {
                args: {
                    type: 'object',
                    properties: {}
                }
            },
            execute: () => {
                const view = findEditorView();
                if (!view) {
                    return;
                }
                toggleBlockComment(view);
            },
            isEnabled
        });
        app.commands.addCommand(CommandIDs.toggleComment, {
            label: trans.__('Toggle Comment'),
            describedBy: {
                args: {
                    type: 'object',
                    properties: {}
                }
            },
            execute: () => {
                const view = findEditorView();
                if (!view) {
                    return;
                }
                toggleComment(view);
            },
            isEnabled
        });
        app.commands.addCommand(CommandIDs.toggleTabFocusMode, {
            label: trans.__('Toggle Tab Focus Mode'),
            caption: trans.__('Toggles behavior of Tab key between inserting indentation and moving to next focusable element'),
            describedBy: {
                args: {
                    type: 'object',
                    properties: {}
                }
            },
            execute: () => {
                const view = findEditorView();
                if (!view) {
                    return;
                }
                toggleTabFocusMode(view);
            },
            isEnabled
        });
        app.commands.addCommand(CommandIDs.selectNextOccurrence, {
            label: trans.__('Select Next Occurrence'),
            describedBy: {
                args: {
                    type: 'object',
                    properties: {}
                }
            },
            execute: () => {
                const view = findEditorView();
                if (!view) {
                    return;
                }
                selectNextOccurrence(view);
            },
            isEnabled
        });
        app.commands.addCommand(CommandIDs.foldCurrent, {
            label: trans.__('Fold Current Region'),
            describedBy: {
                args: {
                    type: 'object',
                    properties: {}
                }
            },
            // Updated execute function using the helper
            execute: () => {
                var _a;
                const editorInfo = getActiveEditorInfo();
                if (editorInfo.type === 'notebook') {
                    editorInfo.notebook.mode = 'edit';
                    if (editorInfo.notebook.activeCell) {
                        (_a = editorInfo.notebook.activeCell.editor) === null || _a === void 0 ? void 0 : _a.focus();
                    }
                }
                else if (editorInfo.type === 'fileEditor') {
                    editorInfo.widget.content.editor.focus();
                }
                const view = findEditorView();
                if (!view) {
                    return;
                }
                const { state } = view;
                const pos = state.selection.main.head;
                const line = state.doc.lineAt(pos);
                const range = foldable(state, line.from, line.to);
                if (range) {
                    foldCode(view);
                }
            },
            isEnabled: isCodeFoldingEnabled
        });
        app.commands.addCommand(CommandIDs.unfoldCurrent, {
            label: trans.__('Unfold Current Region'),
            describedBy: {
                args: {
                    type: 'object',
                    properties: {}
                }
            },
            execute: () => {
                var _a;
                const editorInfo = getActiveEditorInfo();
                if (editorInfo.type === 'notebook') {
                    editorInfo.notebook.mode = 'edit';
                    if (editorInfo.notebook.activeCell) {
                        (_a = editorInfo.notebook.activeCell.editor) === null || _a === void 0 ? void 0 : _a.focus();
                    }
                }
                else if (editorInfo.type === 'fileEditor') {
                    editorInfo.widget.content.editor.focus();
                }
                const view = findEditorView();
                if (!view) {
                    return;
                }
                const { state } = view;
                const pos = state.selection.main.head;
                const line = state.doc.lineAt(pos);
                const range = foldable(state, line.from, line.to);
                if (range) {
                    unfoldCode(view);
                }
            },
            isEnabled: isCodeFoldingEnabled
        });
        app.commands.addCommand(CommandIDs.foldSubregions, {
            label: trans.__('Fold All Subregions'),
            describedBy: {
                args: {
                    type: 'object',
                    properties: {}
                }
            },
            execute: () => {
                var _a;
                const editorInfo = getActiveEditorInfo();
                if (editorInfo.type === 'notebook') {
                    editorInfo.notebook.mode = 'edit';
                    if (editorInfo.notebook.activeCell) {
                        (_a = editorInfo.notebook.activeCell.editor) === null || _a === void 0 ? void 0 : _a.focus();
                    }
                }
                else if (editorInfo.type === 'fileEditor') {
                    editorInfo.widget.content.editor.focus();
                }
                const view = findEditorView();
                if (!view) {
                    return;
                }
                try {
                    const { state } = view;
                    const pos = state.selection.main.head;
                    const line = state.doc.lineAt(pos);
                    const topRegion = foldable(state, line.from, line.to);
                    let hasFoldState = false;
                    try {
                        const currentFoldState = state.field(foldState, false);
                        hasFoldState = currentFoldState !== undefined;
                    }
                    catch (e) {
                        hasFoldState = false;
                    }
                    if (!hasFoldState) {
                        // Prime the folding system with a dummy fold/unfold
                        foldCode(view);
                        unfoldCode(view);
                    }
                    if (!topRegion) {
                        return;
                    }
                    const effects = [];
                    let subPos = topRegion.from + 1;
                    while (subPos < topRegion.to) {
                        const subLine = state.doc.lineAt(subPos);
                        const subRange = foldable(state, subLine.from, subLine.to);
                        if (subRange &&
                            subRange.from > topRegion.from &&
                            subRange.to <= topRegion.to) {
                            effects.push(foldEffect.of(subRange));
                            subPos = subRange.to;
                        }
                        else {
                            subPos = subLine.to + 1;
                        }
                    }
                    if (effects.length > 0) {
                        view.dispatch({ effects });
                    }
                }
                catch (e) {
                    // Silent fail
                }
            },
            isEnabled: isCodeFoldingEnabled
        });
        app.commands.addCommand(CommandIDs.unfoldSubregions, {
            label: trans.__('Unfold All Subregions'),
            describedBy: {
                args: {
                    type: 'object',
                    properties: {}
                }
            },
            execute: () => {
                var _a;
                const editorInfo = getActiveEditorInfo();
                if (editorInfo.type === 'notebook') {
                    editorInfo.notebook.mode = 'edit';
                    if (editorInfo.notebook.activeCell) {
                        (_a = editorInfo.notebook.activeCell.editor) === null || _a === void 0 ? void 0 : _a.focus();
                    }
                }
                else if (editorInfo.type === 'fileEditor') {
                    editorInfo.widget.content.editor.focus();
                }
                const view = findEditorView();
                if (!view) {
                    return;
                }
                try {
                    const { state } = view;
                    const pos = state.selection.main.head;
                    const line = state.doc.lineAt(pos);
                    const topRegion = foldable(state, line.from, line.to);
                    if (!topRegion) {
                        return;
                    }
                    const foldedRanges = state.field(foldState, false);
                    if (!foldedRanges) {
                        return;
                    }
                    const effects = [];
                    foldedRanges.between(topRegion.from + 1, topRegion.to, (from, to) => {
                        if (from > topRegion.from && to <= topRegion.to) {
                            effects.push(unfoldEffect.of({ from, to }));
                        }
                    });
                    if (effects.length > 0) {
                        view.dispatch({ effects });
                    }
                }
                catch (e) {
                    // Silent fail
                }
            },
            isEnabled: isCodeFoldingEnabled
        });
        app.commands.addCommand(CommandIDs.foldAll, {
            label: trans.__('Fold All Regions'),
            describedBy: {
                args: {
                    type: 'object',
                    properties: {}
                }
            },
            execute: () => {
                var _a;
                const editorInfo = getActiveEditorInfo();
                if (editorInfo.type === 'notebook') {
                    editorInfo.notebook.mode = 'edit';
                    if (editorInfo.notebook.activeCell) {
                        (_a = editorInfo.notebook.activeCell.editor) === null || _a === void 0 ? void 0 : _a.focus();
                    }
                }
                else if (editorInfo.type === 'fileEditor') {
                    editorInfo.widget.content.editor.focus();
                }
                const view = findEditorView();
                if (!view) {
                    return;
                }
                try {
                    foldAll(view);
                }
                catch (e) {
                    // Silent fail
                }
            },
            isEnabled: isCodeFoldingEnabled
        });
        app.commands.addCommand(CommandIDs.unfoldAll, {
            label: trans.__('Unfold All Regions'),
            describedBy: {
                args: {
                    type: 'object',
                    properties: {}
                }
            },
            execute: () => {
                var _a;
                const editorInfo = getActiveEditorInfo();
                if (editorInfo.type === 'notebook') {
                    editorInfo.notebook.mode = 'edit';
                    if (editorInfo.notebook.activeCell) {
                        (_a = editorInfo.notebook.activeCell.editor) === null || _a === void 0 ? void 0 : _a.focus();
                    }
                }
                else if (editorInfo.type === 'fileEditor') {
                    editorInfo.widget.content.editor.focus();
                }
                const view = findEditorView();
                if (!view) {
                    return;
                }
                try {
                    unfoldAll(view);
                }
                catch (e) {
                    // Silent fail
                }
            },
            isEnabled: isCodeFoldingEnabled
        });
        if (palette) {
            const category = trans.__('File Operations');
            [
                CommandIDs.foldCurrent,
                CommandIDs.unfoldCurrent,
                CommandIDs.foldSubregions,
                CommandIDs.unfoldSubregions,
                CommandIDs.foldAll,
                CommandIDs.unfoldAll
            ].forEach(command => {
                palette.addItem({ command, category });
            });
        }
    }
};
//# sourceMappingURL=commands.js.map